/**
 * LicenseDisclaimerModal - Shown when adding shared literature with an open license
 *
 * Displays the license terms and requires user to accept responsibility
 * for verifying their own rights to use the material.
 */

import { useState } from 'react';
import { Shield, CheckCircle, ExternalLink, AlertTriangle, X } from 'lucide-react';
import { LICENSE_TYPE_LABELS, CC_VARIANT_LABELS, LicenseType, CCVariant } from './InlineLicenseForm';

interface LicenseInfo {
  license_type: LicenseType;
  cc_variant?: CCVariant | null;
  requires_attribution?: boolean;
  attribution_text?: string;
  allows_modification?: boolean;
  allows_commercial_use?: boolean;
  allows_distribution?: boolean;
  license_url?: string;
  rights_holder?: string;
  valid_until?: string;
  usage_notes?: string;
}

interface SharedLiterature {
  id: string;
  title: string;
  author?: string;
  original_url?: string;
  publisher?: string;
}

interface LicenseDisclaimerModalProps {
  literature: SharedLiterature;
  license: LicenseInfo | null;
  onAccept: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function LicenseDisclaimerModal({
  literature,
  license,
  onAccept,
  onCancel,
  isLoading = false
}: LicenseDisclaimerModalProps) {
  const [accepted, setAccepted] = useState(false);

  const getLicenseLabel = () => {
    if (!license) return 'Ukjent';
    const typeLabel = LICENSE_TYPE_LABELS[license.license_type] || license.license_type;
    if (license.license_type === 'creative_commons' && license.cc_variant) {
      const variantLabel = CC_VARIANT_LABELS[license.cc_variant] || license.cc_variant;
      return `${typeLabel} - ${variantLabel}`;
    }
    return typeLabel;
  };

  const getPermissionsList = () => {
    if (!license) return [];
    const permissions = [];

    if (license.allows_distribution !== false) {
      permissions.push({ label: 'Kan deles videre', allowed: true });
    } else {
      permissions.push({ label: 'Kan ikke deles videre', allowed: false });
    }

    if (license.allows_modification !== false) {
      permissions.push({ label: 'Kan bearbeides', allowed: true });
    } else {
      permissions.push({ label: 'Ingen bearbeidelse tillatt', allowed: false });
    }

    if (license.allows_commercial_use === false) {
      permissions.push({ label: 'Kun ikke-kommersiell bruk', allowed: false });
    }

    if (license.requires_attribution) {
      permissions.push({ label: 'Kreditering påkrevd', allowed: true, required: true });
    }

    return permissions;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Shield className="text-kf-primary" size={24} />
            <h3 className="text-lg font-bold text-gray-900">Lisensbekreftelse</h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded-full transition"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Literature info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="font-medium text-gray-900">{literature.title}</h4>
            {literature.author && (
              <p className="text-sm text-gray-600">Forfatter: {literature.author}</p>
            )}
            {literature.publisher && (
              <p className="text-sm text-gray-600">Utgiver: {literature.publisher}</p>
            )}
            {literature.original_url && (
              <a
                href={literature.original_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-kf-primary hover:underline flex items-center gap-1 mt-1"
              >
                Åpne kilde <ExternalLink size={12} />
              </a>
            )}
          </div>

          {/* License info */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="text-green-600" size={18} />
              <span className="font-medium text-gray-900">Lisens: {getLicenseLabel()}</span>
            </div>

            {license?.license_url && (
              <a
                href={license.license_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-kf-primary hover:underline flex items-center gap-1 mb-2"
              >
                Se fullstendige lisensvilkår <ExternalLink size={12} />
              </a>
            )}

            {/* Permissions */}
            <div className="space-y-1 mt-2">
              {getPermissionsList().map((perm, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${
                    perm.allowed ? 'bg-green-500' : 'bg-yellow-500'
                  }`} />
                  <span className={perm.required ? 'font-medium' : ''}>
                    {perm.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Attribution notice */}
          {license?.requires_attribution && license.attribution_text && (
            <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-kf-blue-800 mb-1">Påkrevd kreditering:</p>
              <p className="text-sm text-kf-blue-700 italic">"{license.attribution_text}"</p>
            </div>
          )}

          {/* Usage notes */}
          {license?.usage_notes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Merknader:</p>
                  <p className="text-sm text-yellow-700">{license.usage_notes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="text-sm text-gray-700">
              Ved å legge til denne litteraturen bekrefter du at:
            </p>
            <ul className="text-sm text-gray-700 mt-2 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-gray-400">•</span>
                Du er selv ansvarlig for å verifisere at du har rett til å bruke materialet
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400">•</span>
                Du vil overholde lisensvilkårene som angitt
              </li>
              {license?.requires_attribution && (
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  Du vil kreditere rettighetshaver som påkrevd
                </li>
              )}
              {license?.allows_modification === false && (
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  Du ikke vil bearbeide eller endre materialet
                </li>
              )}
              {license?.allows_commercial_use === false && (
                <li className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  Du ikke vil bruke materialet til kommersielle formål
                </li>
              )}
            </ul>
          </div>

          {/* Acceptance checkbox */}
          <label className="flex items-start gap-3 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-kf-primary focus:ring-kf-primary"
            />
            <span className="text-sm text-gray-700">
              Jeg bekrefter at jeg har lest og forstått lisensvilkårene, og at jeg er ansvarlig
              for å overholde disse ved bruk av denne litteraturen.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-100 transition"
          >
            Avbryt
          </button>
          <button
            onClick={onAccept}
            disabled={!accepted || isLoading}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
              accepted && !isLoading
                ? 'bg-kf-primary text-white hover:bg-kf-secondary'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                Legger til...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Godta og legg til
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
